// Simple backup system for scanned box items
import { useEffect, useCallback } from 'react';
import { BoxProp } from '../../../types/Shipment.types';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface BoxItemsBackupProps {
  box: BoxProp | null;
  open: boolean;
}

export const useBoxItemsBackup = ({ box, open }: BoxItemsBackupProps) => {
  const dispatch = useAppDispatch();

  // Save box items to localStorage
  const saveBoxItems = useCallback(() => {
    if (!box?.id || !box.boxItems) return;

    const backupKey = `box-items-backup-${box.id}`;
    const backup = {
      boxId: box.id,
      items: box.boxItems,
      timestamp: Date.now(),
      itemCount: box.boxItems.length
    };

    try {
      localStorage.setItem(backupKey, JSON.stringify(backup));
    } catch (error) {
      console.warn('Failed to backup box items:', error);
    }
  }, [box?.id, box?.boxItems]);

  // Check for lost items when dialog opens
  const checkForLostItems = useCallback(() => {
    if (!box?.id) return;

    const backupKey = `box-items-backup-${box.id}`;
    try {
      const backupData = localStorage.getItem(backupKey);
      if (!backupData) return;

      const backup = JSON.parse(backupData);
      const currentCount = box.boxItems?.length || 0;
      const backupCount = backup.itemCount || 0;
      const timeSinceBackup = Date.now() - backup.timestamp;
      const hoursAgo = Math.floor(timeSinceBackup / (1000 * 60 * 60));
      const minutesAgo = Math.floor(timeSinceBackup / (1000 * 60));

      // Only alert if backup is recent (less than 24 hours) and there's a difference
      if (timeSinceBackup < 24 * 60 * 60 * 1000 && backupCount > currentCount) {
        dispatch(
          showAlert({
            message: `⚠️ POSSIBLE DATA LOSS: Found backup with ${backupCount} items from ${
              hoursAgo > 0 ? `${hoursAgo}h ago` : `${minutesAgo}m ago`
            }, but box only has ${currentCount} items now. You may have lost ${backupCount - currentCount} scanned items!`,
            type: 'error'
          })
        );
      } else if (timeSinceBackup > 5 * 60 * 1000 && backupCount === currentCount && backupCount > 0) {
        // Confirmation that items are preserved
        dispatch(
          showAlert({
            message: `✓ Your ${currentCount} scanned items are preserved.`,
            type: 'success'
          })
        );
      }
    } catch (error) {
      console.warn('Failed to check item backup:', error);
    }
  }, [box?.id, box?.boxItems, dispatch]);

  // Auto-save items when they change
  useEffect(() => {
    if (!open || !box?.id) return;

    const timeoutId = setTimeout(() => {
      saveBoxItems();
    }, 2000); // Save 2 seconds after changes

    return () => clearTimeout(timeoutId);
  }, [box?.boxItems, open, saveBoxItems]);

  // Save on page visibility change
  useEffect(() => {
    if (!open || !box?.id) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveBoxItems();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [open, box?.id, saveBoxItems]);

  // Save before page unload
  useEffect(() => {
    if (!open || !box?.id) return;

    const handleBeforeUnload = () => {
      saveBoxItems();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [open, box?.id, saveBoxItems]);

  // Check for lost items when dialog opens
  useEffect(() => {
    if (open && box?.id) {
      const timeoutId = setTimeout(() => {
        checkForLostItems();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [open, box?.id, checkForLostItems]);

  // Clean up backup when dialog closes properly
  const clearBackup = useCallback(() => {
    if (!box?.id) return;

    const backupKey = `box-items-backup-${box.id}`;
    try {
      localStorage.removeItem(backupKey);
    } catch (error) {
      console.warn('Failed to clear backup:', error);
    }
  }, [box?.id]);

  return { saveBoxItems, clearBackup, checkForLostItems };
};